import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Grid,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import InfoIcon from '@mui/icons-material/Info'
import HistoryIcon from '@mui/icons-material/History'
import ErrorIcon from '@mui/icons-material/Error'
import { productService } from '../services'
import { formatDate } from '../utils/dateFormat'
import { getUploadSuccessMessage } from '../utils/uploadSuccessMessages'
import UploadErrorDetailsDialog from '../components/UploadErrorDetailsDialog'
import UploadLogStatusChip from '../components/UploadLogStatusChip'
import UploadHistoryRefreshButton from '../components/UploadHistoryRefreshButton'
import UploadHistoryTableWrapper from '../components/UploadHistoryTableWrapper'
import UploadImportStatusAlert from '../components/UploadImportStatusAlert'
import InstructionCard from '../components/common/InstructionCard'
import { purchasableStatusUploadInstructions } from '../constants/uploadInstructions'
import { useUploadHistoryPolling } from '../hooks/useUploadHistoryPolling'

const PurchasableStatusUploadPage = () => {
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [processedItems, setProcessedItems] = useState(0)
  const [uploadLogs, setUploadLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [errorDetailsDialog, setErrorDetailsDialog] = useState({ open: false, uploadLog: null })
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)
  const logsFetchInFlightRef = useRef(false)

  const handleFileChange = (e) => {
    setFile(e.target.files[0])
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile)
      } else {
        setMessage({ type: 'error', text: 'Please upload a CSV file only' })
      }
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file to upload' })
      return
    }

    try {
      setLoading(true)
      setUploadProgress(0)
      setProcessedItems(0)

      const text = await file.text()
      const lines = text.split('\n').filter((line) => line.trim())
      const itemCount = Math.max(0, lines.length - 1)
      setTotalItems(itemCount)

      const progressInterval = setInterval(() => {
        setProcessedItems((prev) => {
          const next = Math.min(prev + Math.floor(itemCount * 0.1), Math.max(itemCount - 1, 0))
          setUploadProgress(itemCount > 0 ? Math.round((next / itemCount) * 80) : 80)
          return next
        })
      }, 300)

      const result = await productService.importPurchasableStatus({ file })

      clearInterval(progressInterval)
      setProcessedItems(itemCount)
      setUploadProgress(100)

      setMessage({
        type: 'success',
        text: result.message || getUploadSuccessMessage('purchasableStatus'),
        showHistoryLink: true,
      })
      setFile(null)
      const fileInput = document.getElementById('purchasable-status-file-input')
      if (fileInput) fileInput.value = ''
      fetchUploadLogs()

      setTimeout(() => {
        setUploadProgress(0)
        setProcessedItems(0)
        setTotalItems(0)
      }, 2000)
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error uploading purchasable status',
      })
      setUploadProgress(0)
      setProcessedItems(0)
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }

  const handleViewErrorDetails = async (logId) => {
    try {
      const response = await productService.getUploadLogDetails(logId)
      setErrorDetailsDialog({ open: true, uploadLog: response.data || response })
    } catch (error) {
      console.error('Error fetching upload log details:', error)
    }
  }

  const fetchUploadLogs = useCallback(async ({ silent = false } = {}) => {
    if (logsFetchInFlightRef.current) {
      return
    }
    logsFetchInFlightRef.current = true
    try {
      if (!silent) {
        setLogsLoading(true)
      }
      const response = await productService.getPurchasableStatusUploadLogs({
        page: logsPage + 1,
        limit: logsRowsPerPage,
      })

      const logsData = response.items || response.data || response.logs || (Array.isArray(response) ? response : [])
      const totalCount = response.pagination?.total || response.totalCount || response.total || (Array.isArray(logsData) ? logsData.length : 0)

      setUploadLogs(Array.isArray(logsData) ? logsData : [])
      setLogsTotalCount(totalCount)
    } catch (error) {
      console.error('Error fetching upload logs:', error)
      if (!silent) {
        setUploadLogs([])
        setLogsTotalCount(0)
      }
    } finally {
      if (!silent) {
        setLogsLoading(false)
      }
      logsFetchInFlightRef.current = false
    }
  }, [logsPage, logsRowsPerPage])

  const handleDownloadSampleCSV = () => {
    const csv = ['Article Code', '2100048', '2100091'].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'purchasable-status-sample.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleViewUploadHistory = useCallback(() => {
    setActiveTab(1)
    fetchUploadLogs()
  }, [fetchUploadLogs])

  useEffect(() => {
    if (activeTab === 1) {
      fetchUploadLogs()
    }
  }, [activeTab, fetchUploadLogs])

  useUploadHistoryPolling(activeTab === 1, fetchUploadLogs)

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a' }}>
          Purchasable Status
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Upload article codes to set whether each product can be purchased. Status 1 is purchasable and status 0 is not.
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(event, newValue) => setActiveTab(newValue)}>
            <Tab label="Upload" icon={<CloudUploadIcon />} iconPosition="start" />
            <Tab label="Upload History" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  {message && (
                    <UploadImportStatusAlert
                      severity={message.type}
                      onClose={() => setMessage(null)}
                      showHistoryLink={message.showHistoryLink}
                      onViewUploadHistory={handleViewUploadHistory}
                    >
                      {message.text}
                    </UploadImportStatusAlert>
                  )}

                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ color: '#e90000' }} />
                    Upload Article Codes CSV
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        border: isDragOver ? '2px solid #1B5E20' : '2px dashed #e90000',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: isDragOver ? '#E8F5E9' : '#f8f9fb',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: '#E8F5E9',
                          borderColor: '#1B5E20',
                        },
                      }}
                      onClick={() => document.getElementById('purchasable-status-file-input').click()}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <CloudUploadIcon sx={{ fontSize: 56, color: '#e90000', mb: 2 }} />
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        {file ? file.name : isDragOver ? 'Drop CSV file here' : 'Click to upload or drag and drop'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        CSV file with an Article Code column
                      </Typography>
                      <input
                        id="purchasable-status-file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        hidden
                      />
                    </Box>
                  </Box>

                  {loading && uploadProgress > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                          Upload Progress
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {uploadProgress}% ({processedItems}/{totalItems} items)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: '#e90000',
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleUpload}
                    disabled={loading || !file}
                    sx={{
                      bgcolor: '#e90000',
                      py: 1.5,
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#cc0000' },
                    }}
                  >
                    {loading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                    {loading ? 'Uploading...' : 'Upload Article Codes'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <DownloadIcon sx={{ color: '#e90000', fontSize: 40 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Sample CSV
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Download a sample CSV with the Article Code column.
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<DownloadIcon sx={{ fontSize: 20 }} />}
                    onClick={handleDownloadSampleCSV}
                    sx={{
                      color: '#e90000',
                      borderColor: '#e90000',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#E8F5E9' },
                      py: 1.2,
                    }}
                  >
                    Download Sample
                  </Button>
                </CardContent>
              </Card>

              <InstructionCard {...purchasableStatusUploadInstructions} icon={<InfoIcon />} />
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upload History
                </Typography>
                <UploadHistoryRefreshButton
                  onClick={fetchUploadLogs}
                  loading={logsLoading}
                />
              </Box>

              <UploadHistoryTableWrapper
                loading={logsLoading}
                isEmpty={uploadLogs.length === 0}
              >
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                          <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Success</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Failed</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Success Rate</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadLogs.map((log) => (
                          <TableRow key={log.id} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                            <TableCell>{log.file_name}</TableCell>
                            <TableCell sx={{ color: '#666' }}>{formatDate(log.created_at)}</TableCell>
                            <TableCell align="center">{log.total_rows}</TableCell>
                            <TableCell align="center" sx={{ color: '#4caf50', fontWeight: 600 }}>
                              {log.successful_imports}
                            </TableCell>
                            <TableCell align="center" sx={{ color: '#f44336', fontWeight: 600 }}>
                              {log.failed_imports}
                            </TableCell>
                            <TableCell align="center">
                              {log.total_rows > 0
                                ? `${Math.round((log.successful_imports / log.total_rows) * 100)}%`
                                : '0%'}
                            </TableCell>
                            <TableCell>
                              <UploadLogStatusChip status={log.status} />
                            </TableCell>
                            <TableCell align="center">
                              {log.failed_imports > 0 ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<ErrorIcon />}
                                  onClick={() => handleViewErrorDetails(log.id)}
                                >
                                  View {log.failed_imports} Error{log.failed_imports !== 1 ? 's' : ''}
                                </Button>
                              ) : (
                                <span style={{ color: '#999', fontSize: '0.75rem' }}>No errors</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={logsTotalCount}
                    page={logsPage}
                    onPageChange={(event, newPage) => setLogsPage(newPage)}
                    rowsPerPage={logsRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setLogsRowsPerPage(parseInt(event.target.value, 10))
                      setLogsPage(0)
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton
                    showLastButton
                  />
                </>
              </UploadHistoryTableWrapper>
            </CardContent>
          </Card>
        )}
      </Container>

      <UploadErrorDetailsDialog
        open={errorDetailsDialog.open}
        onClose={() => setErrorDetailsDialog({ open: false, uploadLog: null })}
        uploadLog={errorDetailsDialog.uploadLog}
        onExportError={(text) => setMessage({ type: 'error', text })}
      />
    </>
  )
}

export default PurchasableStatusUploadPage
